import {
    type ReactNode
} from "react";
import {
    Platform
} from "react-native";
import {
    initializeInstances
} from "./hooks";
import type {
    NCoreUIKitConfig
} from "../types";
import {
    SafeAreaProvider
} from "react-native-safe-area-context";
import {
    INTERACTION_STATE_SELECTOR
} from "../helpers/interaction/useInteractionState";
import {
    WebScrollbar
} from "../components";
import packageJSON from "../../package.json";
import CoreContext from "../context";

export const getNCoreUIKitVersion = () => packageJSON.version;

export class NCoreUIKitBase<T extends NCoreUIKitConfig> {
    NCoreUIKitContext: CoreContext<T>;

    constructor(initialState: T) {
        this.NCoreUIKitContext = new CoreContext(initialState);
    }

    Provider = ({
        children
    }: {
        children: ReactNode;
    }) => {
        const NCoreUIKitContext = this.NCoreUIKitContext;

        return <SafeAreaProvider>
            <NCoreUIKitContext.Provider>
                <WebScrollbar/>
                {children}
            </NCoreUIKitContext.Provider>
        </SafeAreaProvider>;
    };
};

let NCoreUIKit!: NCoreUIKitBase<NCoreUIKitConfig>;

export const setupNCoreUIKit = (initialState: NCoreUIKitConfig, isCustom: boolean = false) => {
    if(isCustom) {
        return new NCoreUIKitBase(initialState);
    } else {
        NCoreUIKit = new NCoreUIKitBase(initialState);

        initializeInstances(NCoreUIKit);

        if (Platform.OS === "web") {
            const globalRef = globalThis as unknown as { document?: unknown };

            if (globalRef && typeof globalRef.document === "object" && globalRef.document !== null) {
                const doc = globalRef.document as {
                    createElement?: (tagName: string) => unknown;
                    getElementById?: (id: string) => unknown;
                    head?: unknown;
                };

                const styleId = "cl-rnw-touchable-fix";

                if (typeof doc.getElementById === "function" && !doc.getElementById(styleId)) {
                    if (typeof doc.createElement === "function" && doc.head && typeof doc.head === "object") {
                        const style = doc.createElement("style") as {
                            innerHTML?: string;
                            type?: string;
                            id?: string;
                        };

                        if (style) {
                            style.id = styleId;
                            style.type = "text/css";
                            style.innerHTML = `
                                [role="button"]:not(${INTERACTION_STATE_SELECTOR}):not([aria-disabled="true"]),
                                [data-focusable="true"]:not(${INTERACTION_STATE_SELECTOR}):not([aria-disabled="true"]),
                                div[tabindex="0"]:not(${INTERACTION_STATE_SELECTOR}):not([aria-disabled="true"]) {
                                    cursor: pointer;
                                }

                                ${INTERACTION_STATE_SELECTOR}[aria-disabled="true"] {
                                    pointer-events: auto !important;
                                    cursor: not-allowed !important;
                                }

                                ${INTERACTION_STATE_SELECTOR} svg {
                                    position: relative;
                                }

                                :focus:not(:focus-visible) {
                                    outline: none !important;
                                }

                                input:-webkit-autofill,
                                textarea:-webkit-autofill,
                                select:-webkit-autofill {
                                    transition: background-color 600000s 0s, color 600000s 0s;
                                }

                                input:autofill,
                                textarea:autofill,
                                select:autofill {
                                    filter: none;
                                }
                            `;

                            const head = doc.head as { appendChild?: (node: unknown) => void };

                            if (typeof head.appendChild === "function") {
                                head.appendChild(style);
                            }
                        }
                    }
                }
            }
        }

        return NCoreUIKit;
    }
};
